// ============================================================================
// SHARED DATAGRID UTILITIES
// ============================================================================

import { SxProps, Theme } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid';

/**
 * Default DataGrid styling with professional appearance
 */
export const defaultDataGridStyles: SxProps<Theme> = {
  border: 'none',
  minHeight: 400,
  maxHeight: 800,
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f3f4f6',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
  },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  '& .MuiDataGrid-columnHeader': {
    '&:focus': {
      outline: 'none',
    },
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  // Professional column separator styling
  '& .MuiDataGrid-columnSeparator': {
    color: '#e5e7eb',
    '&:hover': {
      color: '#1976d2',
    },
  },
};

/**
 * Default column properties for consistent behavior
 */
export const defaultColumnProps: Partial<GridColDef> = {
  sortable: true,
  filterable: true,
};

/**
 * Helper function to create columns with default properties
 */
export const createStandardColumn = (column: GridColDef): GridColDef => {
  return {
    ...defaultColumnProps,
    ...column,
    // Ensure minimum width is set for flexible columns
    minWidth: column.minWidth || 100,
  };
};

/**
 * Helper function to create columns array with consistent properties
 */
export const createStandardColumns = (columns: GridColDef[]): GridColDef[] => {
  return columns.map(createStandardColumn);
};

// ============================================================================
// RBAC PERMISSION MAP
// ============================================================================

export const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin: [
    'users.read', 'users.write', 'users.delete',
    'content.read', 'content.write', 'content.delete', 'content.moderate',
    'analytics.read', 'analytics.export',
    'playlists.read', 'playlists.write', 'playlists.delete',
    'system.admin',
  ],
  teacher: [
    'content.read',
    'analytics.read',
    'playlists.read', 'playlists.write',
  ],
  moderator: [
    'content.read', 'content.moderate',
    'analytics.read',
  ],
};

export const NAV_PERMISSIONS: Record<string, string> = {
  '/dashboard/users': 'users.read',
  '/dashboard/playlists': 'playlists.read',
  '/dashboard/content': 'content.read',
  '/dashboard/analytics': 'analytics.read',
  '/dashboard/health': 'system.admin',
};

export const hasNavAccess = (path: string, userPermissions: string[]): boolean => {
  const requiredPermission = NAV_PERMISSIONS[path];
  if (!requiredPermission) return true;
  return userPermissions.includes(requiredPermission);
};

// ============================================================================
// CSV EXPORT UTILITY
// ============================================================================

export const downloadCSV = (headers: string[], rows: string[][], filename: string) => {
  const csv = [
    headers.join(','),
    ...rows.map(r => r.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};